import React, { useMemo, useState } from 'react';
import { useTable, useSortBy, useGlobalFilter, usePagination } from 'react-table';
import Moment from 'react-moment';
import Button from 'react-bootstrap/Button';

const ReactTableComponent = ({ 
  assignments, 
  assignPage, 
  viewAssignment, 
  dummy, 
  video_arrow, 
  mic_arrow, 
  ntpd_arrow 
}) => {
  const [globalFilter, setGlobalFilter] = useState('');

    // Helper functions
  const calculateDuration = (durationString) => {
    if (!durationString) return '';
    const durationSplit = durationString.split(':');
    const hours = parseInt(durationSplit[0]);
    const mins = parseInt(durationSplit[1]);
    const secs = parseInt(durationSplit[2]);

    let duration = '';
    if (hours > 0) duration += `${hours}h`;
    if (mins > 0) duration += ` ${mins}m`;
    if (secs > 0) duration += ` ${secs}s`;
    return duration.trim();
  };

  const getAssignmentIcon = (type, video_arrow, mic_arrow, ntpd_arrow) => {
    switch (type) {
      case 'video': return video_arrow;
      case 'audio': return mic_arrow;
      case 'document': return ntpd_arrow;
      default: return '';
    }
  };

  // Prepare data for the table
  const data = useMemo(() => {
    if (!assignments?.list?.requested) return [];
    return assignments.list.requested.map(elem => ({
      ...elem,
      duration: calculateDuration(elem.duration),
      assignmentIcon: getAssignmentIcon(elem.type, video_arrow, mic_arrow, ntpd_arrow),
      assignerName: elem.assigner ? elem.assigner.full_name : "Deleted User",
      assignerPicture: elem.assigner ? elem.assigner.picture : dummy,
      interpreterName: elem.interpreter ? elem.interpreter.full_name : null,
      interpreterPicture: elem.interpreter ? elem.interpreter.picture : null,
      statusText: elem.status,
      hasInterpreter: !!elem.interpreter_id,
      interpreterList: elem.interPretList || []
    }));
  }, [assignments, dummy, video_arrow, mic_arrow, ntpd_arrow]);

  // Column definitions
  const columns = useMemo(() => [
    {
      Header: 'Assignments Name',
      accessor: 'title',
      Cell: ({ row, value }) => (
        <div>
          <h5>{value}</h5>
          <p>
            <span className='text_white_color_half'>{row.original.division_name},</span> 
            <span className='text_brand_color2'>{row.original.category_title}</span>
          </p>
        </div>
      ),
    },
    {
      Header: 'Date & Time',
      accessor: 'created_at',
      Cell: ({ value }) => (
        <div>
          <h5>
            <Moment format="MM/DD/YY">
              {value}
            </Moment>
          </h5>
          <span className='text_brand_color3'>
            <Moment format="hh:mm A">
              {value}
            </Moment>
          </span>
        </div>
      ),
    },
    {
      Header: 'Coordinators',
      id: 'coordinators',
      Cell: ({ row }) => (
        <>
          <td width="50">
            <img className='tbl_usr_img' src={row.original.assignerPicture} alt="Coordinator" />
          </td>
          <td>
            <h5>{row.original.assignerName}</h5>
          </td>
        </>
      ),
      disableSortBy: true,
    },
    {
      Header: 'Format',
      accessor: 'type',
      Cell: ({ row }) => (
        <div className='text-center'>
          <img src={row.original.assignmentIcon} alt={row.original.type} />
        </div>
      ),
      disableSortBy: true,
    },
    {
      Header: 'Interpreter',
      id: 'interpreter',
      Cell: ({ row }) => {
        if (row.original.interpreter) {
          return (
            <>
              <td width="50">
                <img className='tbl_usr_img' src={row.original.interpreterPicture} alt="Interpreter" />
              </td>
              <td>
                <h5>{row.original.interpreterName}</h5>
              </td>
            </>
          );
        } else if (row.original.status === "requested" && row.original.interpreterList.length > 0) {
          return (
            <td colSpan="2" className='text-center'>
              {row.original.interpreterList.map((item, index) => (
                <h5 key={index}>{item?.first_name}{index < row.original.interpreterList.length - 1 ? ', ' : ''}</h5>
              ))}
            </td>
          );
        } else {
          return (
            <td colSpan="2" className='text-center'>
              <h5>--</h5>
            </td>
          );
        }
      },
      disableSortBy: true,
    },
    {
      Header: 'Status',
      accessor: 'statusText',
      Cell: ({ value }) => (
        <span className='text_brand_color6 text-capitalize'>{value}</span>
      ),
    },
    {
      Header: 'Action',
      id: 'actions',
      Cell: ({ row }) => (
        <div className='text-center'>
          {row.original.hasInterpreter ? (
            <Button variant='success' size='sm' disabled={true}>Assigned</Button>
          ) : (
            <Button variant='success' size='sm' onClick={() => assignPage(row.original)}>Assign</Button>
          )}
          &nbsp;|&nbsp;
          <Button variant='dark' size='sm' onClick={() => viewAssignment(row.original)}>View</Button>
        </div>
      ),
      disableSortBy: true,
    },
  ], [assignPage, viewAssignment]);

  // Initialize react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
    setGlobalFilter: setTableGlobalFilter
  } = useTable(
    {
      columns,
      data,
      initialState: { 
        pageIndex: 0, 
        pageSize: 10,
        globalFilter: '' 
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setGlobalFilter(value);
    setTableGlobalFilter(value);
  };

  

  if (!assignments || Object.keys(assignments).length === 0) {
    return null;
  }

  if (assignments.list.requested.length === 0) {
    return (
      <div className="text-center fs-4 py-4">
        No Records Found!
      </div>
    );
  }

  return (
    <div>
      {/* Search Input */}
      <div className="mb-3">
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="input-group">
              {/* <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span> */}
              <input
                type="text"
                className="form-control"
                placeholder="Search assignments..."
                value={globalFilter}
                onChange={handleSearchChange}
                style={{ 'backgroundColor': '#e3e3e3', color: '#000' }}
              />
            </div>
          </div>
          <div className="col-md-6 text-md-end">
            <span className="text-muted">
              Showing {page.length} of {data.length} records
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table {...getTableProps()} className="table table-striped table-hover">
          <thead className="table-dark">
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={column.disableSortBy ? '' : 'sortable'}
                    style={{
                      cursor: column.disableSortBy ? 'default' : 'pointer',
                      minWidth: column.id === 'actions' ? '120px' : 'auto'
                    }}
                  >
                    <div className="d-flex align-items-center">
                      {column.render('Header')}
                      {!column.disableSortBy && (
                        <span className="ms-1">
                          {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="align-middle">
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pageCount > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            <span className="text-muted">
              Page {pageIndex + 1} of {pageOptions.length}
            </span>
          </div>
          
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm"
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              style={{ width: 'auto' }}
            >
              {[5, 10, 20, 50].map(size => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>

            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${!canPreviousPage ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                    «
                  </button>
                </li>
                <li className={`page-item ${!canPreviousPage ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => previousPage()} disabled={!canPreviousPage}>
                    ‹
                  </button>
                </li>
                
                {pageOptions.slice(
                  Math.max(0, pageIndex - 2),
                  Math.min(pageOptions.length, pageIndex + 3)
                ).map(page => (
                  <li key={page} className={`page-item ${pageIndex === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => gotoPage(page)}>
                      {page + 1}
                    </button>
                  </li>
                ))}

                <li className={`page-item ${!canNextPage ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => nextPage()} disabled={!canNextPage}>
                    ›
                  </button>
                </li>
                <li className={`page-item ${!canNextPage ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
                    »
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReactTableComponent;